import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { isMobile } from 'react-device-detect';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import moment from 'moment-timezone';

import { actions } from './redux/actions';
import '../Widgets.scss';
import Strings from '../../../../Strings';
import { Column } from '../../../../containers/Table/components';
import { PageHeader } from '../../../../components/PageHeader';
import { TextInput } from '../../../../components/PageHeader/TextInput';
import Table from '../../../../containers/Table/TableWithPagination';
import { formatTimeSpanHHMMSS, getAlertsReason, makeValid } from '../../../../utils';
import { BILLABLE_TIME_THRESHOLD, DATE_TIME_SPACE_12_UPPERCASE } from '../../../../constants';
import ListOfTables from '../../../../containers/ListOfTables';
import AdvancedFilters, { Filter } from '../../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../../../components/AdvancedFilters/FiltersDefinition';
import { getRelevantFilters } from '../../../../components/AdvancedFilters/helpers';
import AdvancedFiltersBar from '../../../../components/AdvancedFilters/AdvancedFiltersBar';

function PatientsToCall(props) {
  const { data, isLoading, filters, orgIds, refreshTimestamp, getData, onNavigate } = props;
  let searchTime;

  const pageRequest = {
    offset: 0,
    search: '',
    orgId: orgIds && orgIds.length > 0 ? orgIds : undefined,
  };

  useEffect(() => {
    loadData();
  }, [refreshTimestamp, orgIds]);

  const loadData = () => {
    getData(pageRequest);
  };

  const onSearchQueryChange = query => {
    pageRequest.offset = 0;
    pageRequest.search = query;
    clearTimeout(searchTime);
    searchTime = setTimeout(() => {
      getData(pageRequest);
    }, 1000);
  };

  const getHeaderComponents = () => {
    return (
      <>
        <TextInput class="" placeholder={Strings.search} onChange={e => onSearchQueryChange(e.target.value)} />
        <AdvancedFilters>
          <Filter definition={getFiltersDefinition().alertsTypesAndOpenActions} />
        </AdvancedFilters>
      </>
    );
  };

  const onPatientSelected = (_, patient) => {
    if (patient?.patient_id) {
      const maskedId = encodeURIComponent(patient.patient_id);
      onNavigate(`/cap-patients/${maskedId}`);
    }
  };

  const dataForTable = React.useMemo(() => {
    if (!data?.length)
      return [];
    let ret = [...data] || [];
    if (!data?.length) {
      return [];
    }

    const alertFilters = getRelevantFilters(filters);
    if (alertFilters?.length === 0) {
      return ret;
    }

    ret = ret.filter(alert => 
        (alertFilters.some(f => f.value === "enrollment") && (alert.billiable_time < BILLABLE_TIME_THRESHOLD || alert.vitals?.some(v => v.type === 'enrollment')))
        || (alertFilters.some(f => f.value === "vitals") && alert.vitals?.some(v => v.type !== 'enrollment' && v.type !== 'schedule_missed_doses'))
        || (alertFilters.some(f => f.value === "medications") && alert.vitals?.some(v => v.type === 'schedule_missed_doses'))
        || (alertFilters.some(f => f.value === "open_actions") && alert.open_actions?.length > 0)
    );

    return ret;
  }, [data, filters]);

  const getBillableTime = value => {
    const time = formatTimeSpanHHMMSS(value);
    return <div className={value < BILLABLE_TIME_THRESHOLD ? 'red' : ''}>{time}</div>;
  };

  const getAlerts = (time, vitals, open_actions) => {
    const timeAlertOn = !isNaN(time) && time < BILLABLE_TIME_THRESHOLD;
    const vitalsAlertOn = vitals && vitals.some(v => v.type !== 'schedule_missed_doses' && v.type !== 'enrollment');
    const adherenceAlertOn = vitals && vitals.some(v => v.type === 'schedule_missed_doses');
    const actionsOpened = open_actions && open_actions.length > 0;
    const alertOffColor = '#BABABA';

    const timeAlert = (
      <div className="icon-and-text-container" data-tooltip-id="tooltip" data-tooltip-content={timeAlertOn ? Strings.capPatient.alertsTooltips.enrollmentOn : Strings.capPatient.alertsTooltips.enrollment}>
        <div className={`icon-container no-margin ${timeAlertOn ? '' : 'with-border'}`}>
          <div className={`icon-with-border ${timeAlertOn ? 'dollar-on' : 'dollar-off'}`} />
        </div>
      </div>
    );
    const vitalsAlert = (
      <div className="icon-and-text-container" data-tooltip-id="tooltip" data-tooltip-content={vitalsAlertOn ? Strings.capPatient.alertsTooltips.vitalsOn : Strings.capPatient.alertsTooltips.vitals}>
        <div className={`icon-container no-margin ${vitalsAlertOn ? '' : 'with-border'}`}>
          <div className={`icon-with-border ${vitalsAlertOn ? 'heart-on' : 'heart-off'}`} />
        </div>
      </div>
    );
    const adherenceAlert = (
      <div className="icon-and-text-container" data-tooltip-id="tooltip" data-tooltip-content={adherenceAlertOn ? Strings.capPatient.alertsTooltips.medicationsOn : Strings.capPatient.alertsTooltips.medications}>
        <div className={`icon-container no-margin ${adherenceAlertOn ? '' : 'with-border'}`}>
          <div className={`icon-with-border ${adherenceAlertOn ? 'pill-on' : 'pill-off'}`} />
        </div>
      </div>
    );
    const openActionsAlert = (
      <div className="icon-and-text-container" data-tooltip-id="tooltip" data-tooltip-content={actionsOpened ? Strings.capPatient.alertsTooltips.openActionsOn : Strings.capPatient.alertsTooltips.openActions}>
        <div className={`icon-container no-margin ${actionsOpened ? '' : 'with-border'}`}>
          <div className={`icon-with-border ${actionsOpened ? 'tick-on' : 'tick-off'}`} />
        </div>
      </div>
    );

    return (
      <div className="alert-container">
        {timeAlert}
        {vitalsAlert}
        {adherenceAlert}
        {openActionsAlert}
      </div>
    );
  };

  const getFirstAlertDate = vitals => {
    let firstDate = null;
    if (vitals && vitals.filter(v => v.created_at).length > 0) {
      vitals.filter(v => v.created_at).sort((a, b) => moment(a.created_at).unix() - moment(b.created_at).unix());
      firstDate = moment(vitals[0].created_at).format(DATE_TIME_SPACE_12_UPPERCASE);
    }
    return firstDate;
  };

  const columns = [];

  columns.push(<Column key="pharmacy_patient_id" title={Strings.capPatient.patientId} value={e => e.pharmacy_patient_id} />);
  columns.push(<Column key="patientName" title={Strings.capPatient.patientName} value={d => makeValid(d.name)} />);
  columns.push(<Column key="phone" title={Strings.capPatient.mobileNumber} value={d => d?.phone} />);
  columns.push(<Column key="alerts" title={Strings.alerts} value={d => getAlerts(d?.billiable_time, d?.vitals, d?.open_actions)} />);
  columns.push(<Column key="timestamp" title={Strings.dateOfEariestAlert} value={d => getFirstAlertDate(d?.vitals, d?.open_actions)} />);
  columns.push(
    <Column
      key="billableTime"
      title={Strings.billableTime}
      value={d => (!isNaN(d?.billiable_time) ? getBillableTime(d.billiable_time) : '-')}
    />,
  );
  columns.push(<Column key="vitals" title={Strings.comments} value={d => getAlertsReason(d?.vitals, d?.open_actions)} />);

  return (
    <div className="widget-with-table">
      <PageHeader right={() => getHeaderComponents()} left={Strings.patientAlerts} noLeftPadding />
      <AdvancedFiltersBar />
      {isMobile ? (
        <OverlayScrollbarsComponent
          defer
          className="scrollbar-right-margin"
          options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        >
          <ListOfTables
            isLoading={isLoading}
            data={dataForTable || []}
            title={row => row.name}
            rows={columns}
            onClick={patient => onPatientSelected(patient.patient_id, patient)}
          />
        </OverlayScrollbarsComponent>
      ) : (
        <Table
          className="patientsToCall-table"
          isLoading={isLoading}
          name="patients"
          uuid="8295a4d8-61fa-4638-b6be-9b36a96c364d"
          data={dataForTable || []}
          onRowSelection={onPatientSelected}
          enableColumnFiltering
        >
          {columns}
        </Table>
      )}
    </div>
  );
}

PatientsToCall.widgetId = 'PatientsToCallWidget';

PatientsToCall.propTypes = {
  getData: PropTypes.func,
  data: PropTypes.array,
  isLoading: PropTypes.any,
  pagination: PropTypes.any,
  onNavigate: PropTypes.func,
  refreshTimestamp: PropTypes.any.isRequired,
  orgIds: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = state => {
  const { widgetPatientsToCallReducer } = state.dashboardWidgets;
  return {
    data: widgetPatientsToCallReducer && widgetPatientsToCallReducer?.data,
    isLoading: widgetPatientsToCallReducer && widgetPatientsToCallReducer?.isLoading,
    pagination: widgetPatientsToCallReducer && widgetPatientsToCallReducer?.pagination,
    orgIds: state.entities?.orgFilter?.orgIds,
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = dispatch => ({
  getData: pageRequest => dispatch(actions.getPatientsToCall(pageRequest)),
  onNavigate: path => dispatch(push(path)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientsToCall);
